import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styles from '../../styles/cr.module.scss';
import { ListCommandBar } from '../cr/ListCommandBar';
import { MessageDialog } from '../cr/MessageDialog';
import { ConfirmDialog } from '../cr/ConfirmDialog';
import { CrLoadingOverlay } from '../cr/CrLoadingOverlay';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DataContext } from '../DataContext';
import { IPPVision } from '../../types';
import { PPVisionForm } from './PPVisionForm';
import { GraphRenderer } from '../charts/GraphRenderer';

interface PPVisionSingleProps {
    apiConnected: boolean;
    errorHandling?: {
        onError: (msg: string, details?: string) => void;
        clearErrors: () => void;
    };

    lastVisionIdForChilds?: number | null;
    setLastVisionIdForChilds?: React.Dispatch<React.SetStateAction<number | null>>;
}

export const PPVisionSingle: React.FC<PPVisionSingleProps> = ({
    apiConnected,
    errorHandling,
    lastVisionIdForChilds,
    setLastVisionIdForChilds
}) => {
    const history = useHistory();
    const { dataServices } = useContext(DataContext);

    const [loading, setLoading] = useState(false);
    const [entity, setEntity] = useState<IPPVision | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [headingText, setHeadingText] = useState<string>('Vision');
    const [objectivesBtnLabel, setObjectivesBtnLabel] = useState<string>('Objectives');
    const [hideDeleteDialog, setHideDeleteDialog] = useState(true);
    const [hideDeleteDisallowed, setHideDeleteDisallowed] = useState(true);
    const [selectedEntityChildren, setSelectedEntityChildren] = useState<any[]>([]);
    const [isGraph1DataAvailable, setIsGraph1DataAvailable] = useState(false);

    const loadEntity = useCallback(
        async (mode: "current" | "next" | "previous" | "byId" = "current", visionId?: number) => {
            setLoading(true);
            try {
                const visionResult = await dataServices.ppVisionService.readSingleEntity(mode, visionId);

                if (visionResult.vision) {
                    if (mode === "current" && !visionResult.isCurrentFound) {
                        setHeadingText("Vision (No Current Vision)");
                    } else {
                        setHeadingText(
                            visionResult.vision.CurrentVision ? "Vision (Current)" : "Vision"
                        );
                    }

                    setEntity(visionResult.vision);
                    setObjectivesBtnLabel(
                        `Objectives (${visionResult.vision?.PPVisionObjectives?.length ?? 0})`
                    );
                }
            } catch (err: any) {
                errorHandling?.onError(`Error loading Vision (${mode})`, err.message);
            } finally {
                setLoading(false);
            }
        },
        [errorHandling, dataServices]
    );

    useEffect(() => {
        if (apiConnected) {
            if (lastVisionIdForChilds) {
                loadEntity("byId", lastVisionIdForChilds);
            } else {
                loadEntity("current");
            }
        }
    }, [apiConnected, loadEntity, lastVisionIdForChilds]);

    // Command bar items specific to Vision
    const myPreFarItems: ICommandBarItemProps[] = [
        {
            key: 'Current',
            name: 'Current',
            iconProps: { iconName: 'BullseyeTarget' },
            onClick: () => loadEntity("current")
        },
        {
            key: 'Previous',
            name: 'Previous',
            iconProps: { iconName: 'ChevronLeft' },
            onClick: (ev) => {
                ev?.preventDefault();
                if (entity) loadEntity("previous", entity.ID);
            }
        },
        {
            key: 'Next',
            name: 'Next',
            iconProps: { iconName: 'ChevronRight' },
            onClick: () => {
                if (entity) loadEntity("next", entity.ID);
            }
        },
        {
            key: 'Objectives',
            name: objectivesBtnLabel,
            iconProps: { iconName: 'MiniExpand' },
            onClick() {
                const vID = entity?.ID;
                setLastVisionIdForChilds(vID);
                history.push(`/a2/objectives-for-vision/${vID}`);
            }
        }
    ];

    const addEntity = () => {
        setEditMode(false);
        setShowForm(true);
    };

    const editEntity = () => {
        setEditMode(true);
        setShowForm(true);
    };

    const deleteEntity = async () => {
        if (!entity) return;
        errorHandling?.clearErrors();
        setHideDeleteDialog(true);
        try {
            await dataServices.ppVisionService.delete(entity.ID);
            await loadEntity("current"); // fallback to current after delete
        } catch (err: any) {
            errorHandling?.onError(`Error deleting ${entity.Title}`, err.message);
        }
    };

    const checkDelete = async () => {
        if (!entity) return;
        setLoading(true);
        try {
            const children = await dataServices.ppVisionService.entityChildren(entity.ID);
            if (children.length > 0) {
                setSelectedEntityChildren(children);
                setHideDeleteDisallowed(false);
            } else {
                setHideDeleteDialog(false);
            }
        } catch (err: any) {
            errorHandling?.onError('Error checking if item can be deleted.', err.message);
        } finally {
            setLoading(false);
        }
    };

    const entitySaved = () => {
        if (entity) {
            loadEntity("byId", entity.ID); // reload the same entity
        } else {
            loadEntity("current");
        }
        setShowForm(false);
    };

    const runtimeParams = useMemo(
        () => (entity ? { "@VisionID": `${entity.ID}` } : undefined),
        [entity?.ID]
    );


    return (
        <div style={{ width: '100%', position: 'relative' }} className={styles.cr}>
            <CrLoadingOverlay isLoading={loading} opaque={true} />

            {/* Command Bar */}
            <ListCommandBar
                onAdd={addEntity}
                onEdit={editEntity}
                onDelete={checkDelete}
                addDisabled={false}
                editDisabled={!entity}
                viewDisabled={true}
                deleteDisabled={!entity}
                preFarItems={entity ? myPreFarItems : []}
            />

            {/* Title + Description */}
            <div>
                <div style={{ display: 'flex' }}>
                    <div
                        style={{
                            marginTop: '1px',
                            width: '8px',
                            height: '45px',
                            backgroundColor: '#99D9EA'
                        }}
                    />
                    <h2 style={{ flexGrow: 1 }} className={styles.listTitle}>
                        {headingText}
                    </h2>
                </div>

                {entity && (
                    <div style={{ padding: '10px', paddingLeft: '25px' }}>
                        <h3>{entity.Title}</h3>
                        <p style={{ fontStyle: 'italic' }}>{entity.Description}</p>
                    </div>
                )}
            </div>
            {entity && (
                <div className="graph-container">
                    {/* Left one */}
                    <div className="graph-box">
                        <GraphRenderer
                            graphShortname={"ObjectivesDeliveryConfidenceCountPortfoliosBased"}
                            runtimeParams={runtimeParams}
                            onGraphDataFetch={(dataAvailable) => setIsGraph1DataAvailable(dataAvailable)}
                        />
                    </div>

                    {/* Right one */}
                    <div className="graph-box">
                        <GraphRenderer
                            graphShortname={"ObjectivesDeliveryConfidenceCountProjectsBased"}
                            runtimeParams={runtimeParams}
                            onGraphDataFetch={(dataAvailable) => setIsGraph1DataAvailable(dataAvailable)}
                        />
                    </div>
                </div>
            )}



            {/* Form */}
            {showForm && (
                <PPVisionForm
                    errorHandling={errorHandling}
                    showForm={showForm}
                    entityId={editMode ? entity?.ID ?? null : null}
                    onSaved={entitySaved}
                    onCancelled={() => setShowForm(false)}
                />
            )}

            {/* Delete Handling */}
            <MessageDialog
                hidden={hideDeleteDisallowed}
                title="Vision cannot be deleted"
                handleOk={() => setHideDeleteDisallowed(true)}
            >
                <p>Vision cannot be deleted because it has dependent items.</p>
            </MessageDialog>

            <ConfirmDialog
                hidden={hideDeleteDialog}
                title={`Are you sure you want to delete ${entity?.Title || 'this vision'}?`}
                content="Deleting a vision cannot be undone."
                confirmButtonText="Delete"
                handleConfirm={deleteEntity}
                handleCancel={() => setHideDeleteDialog(true)}
            />
        </div>
    );
};
